/**
 * Shared Pressure Attribute Configuration
 *
 * Reusable pressure configs for Filling, Delivery, and Collection activities
 * Always IoT-enabled with centralized source
 */

import * as gqlTypes from "@hydrologiq/gql/graphql"
import type { AttributeConfig } from "../../types"
import { validatePressure as sharedValidatePressure } from "../validators"
import { pressureIoTSource } from "../iot-sources"

// Re-export shared validator for convenience
export { validatePressure } from "../validators"

/**
 * Base pressure configuration
 * Common properties shared across all statuses and activities
 */
export const basePressureConfig: Partial<AttributeConfig> = {
  attribute: gqlTypes.ActivityAttributeKind.Pressure,
  label: "Pressure (bar)",
  validator: sharedValidatePressure,
  order: 30,
  iotSource: pressureIoTSource(), // Always use IoT source
}

/**
 * Pressure configuration for Started status
 * IoT-enabled with manual override
 */
export const pressureForStarted = (): AttributeConfig =>
  ({
    ...basePressureConfig,
    visibility: "iot",
    editable: true,
    required: false,
    helpText: `Pressure at start (from IoT sensor or manual entry, used to calculate hydrogen level)`,
  } as AttributeConfig)

/**
 * Pressure configuration for Completed status
 * IoT-enabled with manual override
 */
export const pressureForCompleted = (): AttributeConfig =>
  ({
    ...basePressureConfig,
    visibility: "iot",
    editable: true,
    required: false,
    helpText: `Pressure at completion (from IoT sensor or manual entry, used to calculate final hydrogen level)`,
  } as AttributeConfig)

/**
 * Hidden pressure configuration for Scheduled status
 * Pressure not relevant during planning phase
 */
export const pressureHidden: AttributeConfig = {
  ...basePressureConfig,
  visibility: "hidden",
  editable: false,
  required: false,
} as AttributeConfig
